/**
 * API service functions for different entities
 * These functions use the base apiClient to make REST calls
 */

import apiClient from "./apiClient.js";
import { ENDPOINTS } from "./endpoints.js";

// ==================== AUTHENTICATION ====================
export const authService = {
  login: (credentials) => apiClient.post(ENDPOINTS.AUTH.LOGIN, credentials),
  logout: () => apiClient.post(ENDPOINTS.AUTH.LOGOUT),
  refresh: () => apiClient.post(ENDPOINTS.AUTH.REFRESH),
  register: (userData) => apiClient.post(ENDPOINTS.AUTH.REGISTER, userData),
  getCurrentUser: () => apiClient.checkAuth(),
};

// ==================== PATIENTS ====================
export const patientService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.PATIENTS.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.PATIENTS.GET(id)),
  create: (patientData) =>
    apiClient.post(ENDPOINTS.PATIENTS.CREATE, patientData),
  update: (id, patientData) =>
    apiClient.put(ENDPOINTS.PATIENTS.UPDATE(id), patientData),
  delete: (id) => apiClient.delete(ENDPOINTS.PATIENTS.DELETE(id)),
  search: (query) => apiClient.get(ENDPOINTS.PATIENTS.SEARCH, { q: query }),
};

// ==================== SERVICES ====================
export const serviceService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.SERVICES.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.SERVICES.GET(id)),
  create: (serviceData) =>
    apiClient.post(ENDPOINTS.SERVICES.CREATE, serviceData),
  update: (id, serviceData) =>
    apiClient.put(ENDPOINTS.SERVICES.UPDATE(id), serviceData),
  delete: (id) => apiClient.delete(ENDPOINTS.SERVICES.DELETE(id)),
};

// ==================== APPOINTMENTS ====================
export const appointmentService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.APPOINTMENTS.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.APPOINTMENTS.GET(id)),
  create: (appointmentData) =>
    apiClient.post(ENDPOINTS.APPOINTMENTS.CREATE, appointmentData),
  update: (id, appointmentData) =>
    apiClient.put(ENDPOINTS.APPOINTMENTS.UPDATE(id), appointmentData),
  delete: (id) => apiClient.delete(ENDPOINTS.APPOINTMENTS.DELETE(id)),
  getByDate: (date) => apiClient.get(ENDPOINTS.APPOINTMENTS.BY_DATE, { date }),
  getByPatient: (patientId) =>
    apiClient.get(ENDPOINTS.APPOINTMENTS.BY_PATIENT(patientId)),
  getAvailableSlots: (params) =>
    apiClient.get(ENDPOINTS.APPOINTMENTS.AVAILABLE_SLOTS, params),
};

// ==================== DOCTOR AVAILABILITY ====================
export const doctorAvailabilityService = {
  getAll: (params = {}) =>
    apiClient.get(ENDPOINTS.DOCTOR_AVAILABILITY.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.DOCTOR_AVAILABILITY.GET(id)),
  create: (availabilityData) =>
    apiClient.post(ENDPOINTS.DOCTOR_AVAILABILITY.CREATE, availabilityData),
  update: (id, availabilityData) =>
    apiClient.put(ENDPOINTS.DOCTOR_AVAILABILITY.UPDATE(id), availabilityData),
  delete: (id) => apiClient.delete(ENDPOINTS.DOCTOR_AVAILABILITY.DELETE(id)),
  getByDoctor: (doctorId) =>
    apiClient.get(ENDPOINTS.DOCTOR_AVAILABILITY.BY_DOCTOR(doctorId)),
};

// ==================== ROOM AVAILABILITY ====================
export const roomAvailabilityService = {
  getAll: (params = {}) =>
    apiClient.get(ENDPOINTS.ROOM_AVAILABILITY.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.ROOM_AVAILABILITY.GET(id)),
  create: (availabilityData) =>
    apiClient.post(ENDPOINTS.ROOM_AVAILABILITY.CREATE, availabilityData),
  update: (id, availabilityData) =>
    apiClient.put(ENDPOINTS.ROOM_AVAILABILITY.UPDATE(id), availabilityData),
  delete: (id) => apiClient.delete(ENDPOINTS.ROOM_AVAILABILITY.DELETE(id)),
  getByRoom: (roomId) =>
    apiClient.get(ENDPOINTS.ROOM_AVAILABILITY.BY_ROOM(roomId)),
};

// ==================== BLOOD TESTS ====================
export const bloodTestService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.BLOOD_TESTS.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.BLOOD_TESTS.GET(id)),
  create: (testData) => apiClient.post(ENDPOINTS.BLOOD_TESTS.CREATE, testData),
  update: (id, testData) =>
    apiClient.put(ENDPOINTS.BLOOD_TESTS.UPDATE(id), testData),
  delete: (id) => apiClient.delete(ENDPOINTS.BLOOD_TESTS.DELETE(id)),
  getByPatient: (patientId) =>
    apiClient.get(ENDPOINTS.BLOOD_TESTS.BY_PATIENT(patientId)),
  upload: (file, additionalData) =>
    apiClient.upload(ENDPOINTS.BLOOD_TESTS.UPLOAD, file, additionalData),
};

// ==================== CLINICAL NOTES ====================
export const clinicalNoteService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.CLINICAL_NOTES.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.CLINICAL_NOTES.GET(id)),
  create: (noteData) =>
    apiClient.post(ENDPOINTS.CLINICAL_NOTES.CREATE, noteData),
  update: (id, noteData) =>
    apiClient.put(ENDPOINTS.CLINICAL_NOTES.UPDATE(id), noteData),
  delete: (id) => apiClient.delete(ENDPOINTS.CLINICAL_NOTES.DELETE(id)),
  getByPatient: (patientId) =>
    apiClient.get(ENDPOINTS.CLINICAL_NOTES.BY_PATIENT(patientId)),
};

// ==================== PRESCRIPTIONS ====================
export const prescriptionService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.PRESCRIPTIONS.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.PRESCRIPTIONS.GET(id)),
  create: (prescriptionData) =>
    apiClient.post(ENDPOINTS.PRESCRIPTIONS.CREATE, prescriptionData),
  update: (id, prescriptionData) =>
    apiClient.put(ENDPOINTS.PRESCRIPTIONS.UPDATE(id), prescriptionData),
  delete: (id) => apiClient.delete(ENDPOINTS.PRESCRIPTIONS.DELETE(id)),
  getByPatient: (patientId) =>
    apiClient.get(ENDPOINTS.PRESCRIPTIONS.BY_PATIENT(patientId)),
};

// ==================== BILLING & INVOICES ====================
export const billingService = {
  getInvoices: (params = {}) =>
    apiClient.get(ENDPOINTS.BILLING.INVOICES, params),
  getInvoice: (id) => apiClient.get(ENDPOINTS.BILLING.INVOICE_DETAIL(id)),
  createInvoice: (data) =>
    apiClient.post(ENDPOINTS.BILLING.CREATE_INVOICE, data),
  updateInvoice: (id, data) =>
    apiClient.put(ENDPOINTS.BILLING.UPDATE_INVOICE(id), data),
  deleteInvoice: (id) => apiClient.delete(ENDPOINTS.BILLING.DELETE_INVOICE(id)),
  finalizeInvoice: (id) => apiClient.post(ENDPOINTS.BILLING.FINALIZE(id)),
  syncInvoice: (id) => apiClient.post(ENDPOINTS.BILLING.SYNC(id)),
  getXeroStatus: () => apiClient.get(ENDPOINTS.BILLING.XERO_STATUS),
  connectXero: () => apiClient.get(ENDPOINTS.BILLING.XERO_CONNECT),
  disconnectXero: () => apiClient.post(ENDPOINTS.BILLING.XERO_DISCONNECT),
};

// ==================== INVENTORY ====================
export const inventoryService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.INVENTORY.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.INVENTORY.GET(id)),
  create: (itemData) => apiClient.post(ENDPOINTS.INVENTORY.CREATE, itemData),
  update: (id, itemData) =>
    apiClient.put(ENDPOINTS.INVENTORY.UPDATE(id), itemData),
  delete: (id) => apiClient.delete(ENDPOINTS.INVENTORY.DELETE(id)),
  getLowStock: () => apiClient.get(ENDPOINTS.INVENTORY.LOW_STOCK),
  getExpiring: () => apiClient.get(ENDPOINTS.INVENTORY.EXPIRING),
  getTransactions: (params = {}) =>
    apiClient.get(ENDPOINTS.INVENTORY.TRANSACTIONS, params),
};

// ==================== REPORTS ====================
export const reportService = {
  getPatientReport: (patientId) =>
    apiClient.get(ENDPOINTS.REPORTS.PATIENT_REPORT(patientId)),
  getBiomarkerTrends: (patientId, params = {}) =>
    apiClient.get(ENDPOINTS.REPORTS.BIOMARKER_TRENDS(patientId), params),
  getHealthScore: (patientId) =>
    apiClient.get(ENDPOINTS.REPORTS.HEALTH_SCORE(patientId)),
  getPracticeMetrics: (params = {}) =>
    apiClient.get(ENDPOINTS.REPORTS.PRACTICE_METRICS, params),
  getRevenue: (params = {}) => apiClient.get(ENDPOINTS.REPORTS.REVENUE, params),
};

// ==================== USERS ====================
export const userService = {
  getAll: (params = {}) => apiClient.get(ENDPOINTS.USERS.LIST, params),
  getById: (id) => apiClient.get(ENDPOINTS.USERS.GET(id)),
  create: (userData) => apiClient.post(ENDPOINTS.USERS.CREATE, userData),
  update: (id, userData) => apiClient.put(ENDPOINTS.USERS.UPDATE(id), userData),
  delete: (id) => apiClient.delete(ENDPOINTS.USERS.DELETE(id)),
  getProfile: () => apiClient.get(ENDPOINTS.USERS.PROFILE),
  updateProfile: (profileData) =>
    apiClient.put(ENDPOINTS.USERS.PROFILE, profileData),
};

// ==================== SETTINGS ====================
export const settingsService = {
  getClinicProfile: () => apiClient.get(ENDPOINTS.SETTINGS.CLINIC_PROFILE),
  updateClinicProfile: (profileData) =>
    apiClient.put(ENDPOINTS.SETTINGS.CLINIC_PROFILE, profileData),
  getAppSettings: () => apiClient.get(ENDPOINTS.SETTINGS.APP_SETTINGS),
  updateAppSettings: (settingsData) =>
    apiClient.put(ENDPOINTS.SETTINGS.APP_SETTINGS, settingsData),
  getRooms: () => apiClient.get(ENDPOINTS.SETTINGS.ROOMS),
  createRoom: (roomData) => apiClient.post(ENDPOINTS.SETTINGS.ROOMS, roomData),
  getEquipment: () => apiClient.get(ENDPOINTS.SETTINGS.EQUIPMENT),
  createEquipment: (equipmentData) =>
    apiClient.post(ENDPOINTS.SETTINGS.EQUIPMENT, equipmentData),
};

// ==================== ANALYTICS ====================
export const analyticsService = {
  getDashboardStats: () => apiClient.get(ENDPOINTS.ANALYTICS.DASHBOARD_STATS),
  getPatientMetrics: (params = {}) =>
    apiClient.get(ENDPOINTS.ANALYTICS.PATIENT_METRICS, params),
  getRevenueAnalytics: (params = {}) =>
    apiClient.get(ENDPOINTS.ANALYTICS.REVENUE_ANALYTICS, params),
  getAppointmentAnalytics: (params = {}) =>
    apiClient.get(ENDPOINTS.ANALYTICS.APPOINTMENT_ANALYTICS, params),
};

// ==================== FILES ====================
export const fileService = {
  upload: (file, additionalData = {}) =>
    apiClient.upload(ENDPOINTS.FILES.UPLOAD, file, additionalData),
  delete: (fileId) => apiClient.delete(ENDPOINTS.FILES.DELETE(fileId)),
};
